// IndexedDB (WEB.md "Storage"): articles, generated levels, reading positions, narration audio and small state.
// One database per Depth API (so a local test server never mixes with the real library); it's wiped when a different
// account signs in.
import { API_BASE } from "../config";

const STORES = ["kv", "articles", "levels", "positions", "audio", "ask"] as const;
export type StoreName = (typeof STORES)[number];

let dbp: Promise<IDBDatabase> | null = null;

function dbName(): string {
  let h = 0;
  for (const c of API_BASE) h = (h * 31 + c.charCodeAt(0)) | 0;
  return "depth-" + (API_BASE.includes("api.readdepth.app") ? "prod" : (h >>> 0).toString(36));
}

function open(): Promise<IDBDatabase> {
  if (!dbp) {
    dbp = new Promise((resolve, reject) => {
      const req = indexedDB.open(dbName(), 1);
      req.onupgradeneeded = () => { for (const s of STORES) if (!req.result.objectStoreNames.contains(s)) req.result.createObjectStore(s); };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbp;
}

function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => { req.onsuccess = () => resolve(req.result); req.onerror = () => reject(req.error); });
}

export async function get<T = any>(store: StoreName, key: string): Promise<T | undefined> {
  const db = await open();
  return wrap(db.transaction(store).objectStore(store).get(key)) as Promise<T | undefined>;
}

export async function put(store: StoreName, key: string, value: unknown): Promise<void> {
  const db = await open();
  await wrap(db.transaction(store, "readwrite").objectStore(store).put(value, key));
}

export async function del(store: StoreName, key: string): Promise<void> {
  const db = await open();
  await wrap(db.transaction(store, "readwrite").objectStore(store).delete(key));
}

export async function all<T = any>(store: StoreName): Promise<[string, T][]> {
  const db = await open();
  const os = db.transaction(store).objectStore(store);
  const [keys, values] = await Promise.all([wrap(os.getAllKeys()), wrap(os.getAll())]);
  return keys.map((k, i) => [String(k), values[i] as T]);
}

/** Deletes every key starting with `prefix` (e.g. an article's audio). */
export async function delPrefix(store: StoreName, prefix: string): Promise<void> {
  const db = await open();
  const os = db.transaction(store, "readwrite").objectStore(store);
  await wrap(os.delete(IDBKeyRange.bound(prefix, prefix + "￿")));
}

export async function clearAll(): Promise<void> {
  const db = await open();
  const tx = db.transaction([...STORES], "readwrite");
  await Promise.all(STORES.map((s) => wrap(tx.objectStore(s).clear())));
}
